import { useEffect, useRef, useState } from 'react';
import { Building2, Check, ChevronDown, Search } from 'lucide-react';
import { apiClient } from '@kss/api';
import { setActiveBranch, useActiveBranch, type Branch } from './branchStore';

/**
 * Topbar branch picker.
 *
 * Loads `/tenancy/Branch` on first focus, lets the user pick one, and
 * persists the choice via `branchStore` (which the apiClient interceptor
 * reads on every request). Switching branch fires a window event so any
 * page with a live list can re-fetch.
 */

interface BranchRow extends Record<string, unknown> {
  branchId?: string;
  id?: number | string;
  code?: string;
  name?: string;
  isActive?: number | boolean;
}

export default function BranchSwitcher() {
  const active = useActiveBranch();
  const [open, setOpen] = useState(false);
  const [rows, setRows] = useState<BranchRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [query, setQuery] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const fetched = useRef(false);

  // Close on outside click.
  useEffect(() => {
    function onDoc(e: MouseEvent) {
      if (!containerRef.current?.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  async function loadOnce() {
    if (fetched.current) return;
    fetched.current = true;
    setLoading(true);
    setErr(null);
    try {
      const r = await apiClient.get('/tenancy/Branch');
      const d = r.data;
      let list: BranchRow[] = [];
      if (Array.isArray(d)) list = d as BranchRow[];
      else if (d && typeof d === 'object') {
        const obj = d as { items?: unknown[]; data?: unknown[] | { items?: unknown[] } };
        if (Array.isArray(obj.items)) list = obj.items as BranchRow[];
        else if (Array.isArray(obj.data)) list = obj.data as BranchRow[];
        else if (obj.data && Array.isArray((obj.data as { items?: unknown[] }).items)) {
          list = (obj.data as { items: unknown[] }).items as BranchRow[];
        }
      }
      setRows(list);
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Failed to load branches');
    } finally {
      setLoading(false);
    }
  }

  function pick(row: BranchRow) {
    const id = String(row.branchId ?? row.id ?? '');
    if (!id) return;
    const branch: Branch = {
      id,
      code: row.code != null ? String(row.code) : undefined,
      name: String(row.name ?? row.code ?? id.slice(0, 8)),
    };
    setActiveBranch(branch);
    setOpen(false);
    // Notify pages to refetch.
    window.dispatchEvent(new CustomEvent('kss:branch-changed', { detail: branch }));
  }

  function clear() {
    setActiveBranch(null);
    setOpen(false);
    window.dispatchEvent(new CustomEvent('kss:branch-changed', { detail: null }));
  }

  const filtered = rows.filter((r) => {
    const q = query.trim().toLowerCase();
    if (!q) return true;
    return [r.name, r.code]
      .filter(Boolean)
      .map((v) => String(v).toLowerCase())
      .some((v) => v.includes(q));
  });

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        onClick={() => {
          setOpen((o) => !o);
          void loadOnce();
        }}
        title={active ? `Active branch: ${active.name}` : 'Pick a branch'}
        aria-label="Branch switcher"
        className="inline-flex h-7 items-center gap-1.5 rounded-md border border-border bg-surface-card px-2 text-[11px] hover:border-primary"
      >
        <Building2 size={12} className="text-content-muted" />
        <span className="max-w-[120px] truncate font-medium">
          {active?.name ?? 'All branches'}
        </span>
        <ChevronDown size={12} className="text-content-muted" />
      </button>

      {open && (
        <div className="absolute right-0 z-40 mt-1 w-[260px] overflow-hidden rounded-md border border-border bg-surface-card shadow-lg">
          <div className="relative border-b border-border">
            <Search size={12} className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-content-muted" />
            <input
              autoFocus
              className="h-9 w-full bg-transparent px-3 pl-7 text-xs text-content-primary outline-none"
              placeholder="Search branches…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
          </div>
          <div className="max-h-[260px] overflow-y-auto">
            <button
              type="button"
              onClick={clear}
              className="flex w-full items-center justify-between gap-2 px-3 py-2 text-left text-xs hover:bg-surface-card-hover"
            >
              <span className="text-content-primary">All branches</span>
              {!active && <Check size={12} className="text-primary" />}
            </button>
            {loading && (
              <div className="px-3 py-2 text-[11px] text-content-muted">Loading…</div>
            )}
            {err && (
              <div className="px-3 py-2 text-[11px] text-danger">{err}</div>
            )}
            {!loading && filtered.length === 0 && !err && (
              <div className="px-3 py-2 text-[11px] text-content-muted">
                No branches yet — add one under <em>Tenancy → Branches</em>.
              </div>
            )}
            {filtered.map((r) => {
              const id = String(r.branchId ?? r.id ?? '');
              const selected = active?.id === id;
              return (
                <button
                  key={id}
                  type="button"
                  onClick={() => pick(r)}
                  className="flex w-full items-center justify-between gap-2 border-t border-border/40 px-3 py-2 text-left text-xs hover:bg-surface-card-hover"
                >
                  <div className="min-w-0">
                    <div className="truncate text-content-primary">{r.name ?? r.code ?? id.slice(0, 8)}</div>
                    {r.code && (
                      <div className="truncate text-[10px] text-content-muted">{String(r.code)}</div>
                    )}
                  </div>
                  {selected && <Check size={12} className="text-primary" />}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
